import { MoreHorizontal, Plus, Settings2, Sparkles, Trash2 } from "lucide-react";
import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, Skeleton } from "@/components/ui/feedback";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader } from "@/components/layout/page";
import { SystemUpdateFormDialog } from "@/features/system-update-form";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSaveUiPrefs, useTenants, useUiPrefs } from "@/lib/queries";
import {
  SEEN_PREF, formatUpdateDate, kindOf, newestFirst, seenToRecord, useDeleteSystemUpdate,
  useSystemUpdates, type SystemUpdate,
} from "@/lib/system-updates";
import { atLeast } from "@/lib/types";

/**
 * System updates (ACL-631, ADR-205 in platform): the console's changelog, newest first, for
 * everybody signed in. An admin posts their own organisation's; a superadmin the platform's, which
 * every tenant reads. Nothing here touches the notifications.
 *
 * Opening the page records the newest entry's date as seen (`lib/system-updates.ts`), which clears
 * the sidebar's "New" — but not while an admin is signed in as somebody else (ADR-040), as a tour
 * does not record then either.
 */
export function SystemUpdatesPage() {
  const { user: actor } = useAuth();
  const toast = useToast();
  const canPost = atLeast(actor?.role, "admin");
  const isSuperadmin = actor?.role === "superadmin";
  const { data, isPending, error, refetch } = useSystemUpdates();
  const { data: tenants } = useTenants({ enabled: isSuperadmin });
  const prefs = useUiPrefs();
  const savePrefs = useSaveUiPrefs();
  const remove = useDeleteSystemUpdate();

  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<SystemUpdate | undefined>();
  const [removing, setRemoving] = React.useState<SystemUpdate | undefined>();

  const updates = React.useMemo(() => (data ? newestFirst(data) : []), [data]);

  const recorded = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!data || actor?.signed_in_by) return;
    const at = seenToRecord(data, prefs.data);
    if (at === null || recorded.current === at) return;
    recorded.current = at;
    savePrefs.mutate({ [SEEN_PREF]: at });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, prefs.data, actor?.signed_in_by]);

  const openForm = (update?: SystemUpdate) => {
    setEditing(update);
    setFormOpen(true);
  };

  const confirmRemove = async () => {
    if (!removing) return;
    try {
      await remove.mutateAsync(removing.id);
      toast.success("Update removed", removing.title);
      setRemoving(undefined);
    } catch (e) {
      toast.error(
        "Could not remove the update",
        e instanceof ApiError ? e.message : "Please try again.",
      );
    }
  };

  const postButton = canPost ? (
    <Button variant="default" onClick={() => openForm()}>
      <Plus />
      Post update
    </Button>
  ) : undefined;

  const audience = (u: SystemUpdate): string | null => {
    if (u.tenant_id === null) return null;
    if (isSuperadmin) return tenants?.find((t) => t.id === u.tenant_id)?.name ?? "One tenant";
    return "Your organisation";
  };

  return (
    <Page>
      <PageHeader
        title="System updates"
        description="What is new in the console: new features, improvements and fixes, newest first."
        action={postButton}
      />
      <Card className="overflow-hidden">
        {isPending ? (
          <div className="divide-y divide-border" aria-busy>
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-2 px-5 py-4">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-full" />
              </div>
            ))}
          </div>
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load the updates"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : updates.length === 0 ? (
          <EmptyState
            icon={Sparkles}
            title="No updates yet"
            description={
              canPost
                ? "Post the first: a new feature, an improvement or a fix the team should know about."
                : "When something in the console changes, it is written up here."
            }
            action={postButton}
          />
        ) : (
          <ol className="divide-y divide-border" aria-label="System updates">
            {updates.map((u) => {
              const kind = kindOf(u.kind);
              const scope = audience(u);
              return (
                <li
                  key={u.id}
                  className="flex flex-col gap-1 px-5 py-4 sm:flex-row sm:gap-6"
                  data-update={u.id}
                >
                  <time
                    dateTime={u.published_at}
                    className="shrink-0 pt-0.5 text-xs text-text-muted sm:w-28"
                  >
                    {formatUpdateDate(u.published_at)}
                  </time>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge tone={kind.tone} size="sm">
                        {kind.label}
                      </Badge>
                      {scope && (
                        <Badge tone="outline" size="sm">
                          {scope}
                        </Badge>
                      )}
                    </div>
                    <h2 className="text-sm font-medium text-text">{u.title}</h2>
                    <p className="whitespace-pre-line text-sm text-text-muted">{u.body}</p>
                  </div>
                  {u.editable && (
                    <div className="shrink-0 self-start">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${u.title}`}>
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => openForm(u)}>
                            <Settings2 />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={() => setRemoving(u)}>
                            <Trash2 />
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </Card>

      <SystemUpdateFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        update={editing}
        isSuperadmin={isSuperadmin}
      />
      <ConfirmDialog
        open={removing !== undefined}
        onOpenChange={(open) => !open && setRemoving(undefined)}
        title="Remove this update?"
        description={removing ? `"${removing.title}" leaves System updates for everybody who reads it.` : undefined}
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => void confirmRemove()}
      />
    </Page>
  );
}
